import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState
} from "react";
import { Avatar, Button, Col, Divider, Dropdown, Image, Popover, Row, Space, Spin, Tag, Typography, message as antdMessage } from "antd";
import { getInitials } from "../Conversation";
import { MessageOutlined, MailOutlined, PhoneOutlined, CalendarOutlined, MoreOutlined, FileTextOutlined, UnorderedListOutlined, AppstoreOutlined, InfoCircleOutlined, DownOutlined, ReloadOutlined, CopyOutlined } from "@ant-design/icons";
import { HiMiniArrowTurnUpLeft } from "react-icons/hi2";
import { getStatusIcon } from "components/shared-components/AvatarStatus";
import moment from "moment";
import Scrollbars from "react-custom-scrollbars-2";
import Flex from "components/shared-components/Flex";
import bg from "../../../../../assets/image/web-chat.png";
import Title from "antd/es/skeleton/Title";
import apiClient from "services/apiClient";
import ChatSkeleton, { LoadMoreSkeleton } from './ChatSkeleton';

import { useMyContext } from "Context/MyContextProvider";
import {
  hasRestrictedOriginalMessage,
  resolveOriginalMessageForCopy,
} from "utils/restrictedMessageCrypto";
import { renderTextWithLinks } from "./CustomLinkPreview";
import RanderTemplatePreview from "./RanderTemplatePreview";
import RanderListItemPreview from "./RanderListItemPreview";
import { GetPresets } from "../../ListItem/Components/ListMessagePresets";
import RanderCarouselPreview from "./RanderCarouselPreview";
import { GetCaruselPresets } from "../../Carousel/Preset/PresetList";
import { capitalize } from "lodash";
import { motion } from "framer-motion";
import { landingButtonGradient } from "../../LandingPages/components/LandingUtils";
import FileAttachmentCard from "../../components/FileAttachmentCard";
import VideoPlayerModal from "../../components/VideoPlayerModal";
import VideoPreviewThumbnail from "../../components/VideoPreviewThumbnail";
import {
  downloadReportMediaFile,
  resolveAudioDownloadName,
} from "../fileDownloadUtils";
import { resolveDocumentMeta } from "../documentUtils";
import IncomingImagePreview from "./IncomingImagePreview";
import IncomingAudioMessage from "./IncomingAudioMessage";
import LocationMessage from "./LocationMessage";
import QuotedMessagePreview from "./QuotedMessagePreview";
import SwipeToReplyWrapper from "./SwipeToReplyWrapper";
import { isLocationMessage } from "../locationUtils";
import MediaGroupGrid from "./MediaGroupGrid";
import MediaGroupViewer from "./MediaGroupViewer";
import {
  groupContainsImage,
  groupMessagesForDisplay,
} from "../mediaGroupUtils";
import {
  clearChatScrollPosition,
  loadChatScrollPosition,
  saveChatScrollPosition,
} from "../chatScrollStorage";
import { highlightSearchText } from "../chatMessageSearchUtils";

const { Text } = Typography;
const style = {
  oppositeBubble: {
    backgroundColor: "#ffff",
    color: "#000",
    marginLeft: "20px",
    minWidth: "13rem",
    maxWidth: "50rem",
    wordWrap: "break-word",
    padding: "0 10px 10px 0",
  },
};
const MEDIA_PLACEHOLDER = "/img/others/dummy-header.jpg";

const resolveMessageMediaUrl = (message) => {
  if (message?.media_url) {
    return message.media_url;
  }
  const text = String(message?.text || "");
  if (/^https?:\/\//i.test(text)) {
    return text;
  }
  return null;
};

const SCROLL_BOTTOM_THRESHOLD = 50;

const formatAssignmentSystemText = (text, viewerName) => {
  const name = String(viewerName || "").trim();
  if (!name || !text) {
    return text;
  }

  return text.split(name).join("You");
};

const resolveOutgoingAgentUserId = (agentData) => {
  if (!agentData) {
    return null;
  }

  if (agentData.user_id !== undefined && agentData.user_id !== null) {
    return agentData.user_id;
  }

  return agentData.id ?? null;
};

const shouldShowOutgoingAgentAvatar = (message, viewerUserId) => {
  if (message?.from !== "me" || !message?.agent_id) {
    return false;
  }

  const agentUserId = resolveOutgoingAgentUserId(message.agent_id);
  if (agentUserId === null || viewerUserId === null || viewerUserId === undefined) {
    return true;
  }

  return String(agentUserId) !== String(viewerUserId);
};

const getLastConversationMessage = (messages) => {
  if (!Array.isArray(messages)) {
    return null;
  }

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index]?.msgType !== "date") {
      return messages[index];
    }
  }

  return null;
};

const getMessageFingerprint = (message) => {
  if (!message) {
    return "";
  }

  return [
    message.id,
    message.message_id,
    message.msgType,
    message.preset_name,
    message.text,
    message.status,
  ].join("|");
};

const ChatContentBody = forwardRef(({
  messages,
  loading,
  keywords,
  number,
  contactName,
  contactId,
  setLoading,
  onLoadMore,
  getChats,
  onUpdateMessage,
  activeSearchMatchId = null,
  searchHighlightQuery = "",
  onReply,
  parentNumber = "",
  onRegisterSetHasMore,
}, ref) => {
  const { api, authToken, UserData, isMobile } =
    useMyContext();
  const chatBodyRef = useRef();
  const isProgrammaticScrollRef = useRef(false);
  const scrollRestoredForContactRef = useRef(null);
  const restoringPagesRef = useRef(false);
  const pagesRestoreDoneRef = useRef(null);
  const pendingRestoreRef = useRef(null);
  const contactIdRef = useRef(contactId);
  const currentPageRef = useRef(1);
  const justLoadedMoreRef = useRef(false);
  const scrollStateByContactRef = useRef({});
  const restoreRetryTimeoutRef = useRef(null);
  const isRestoringScrollRef = useRef(false);
  const skipScrollSaveRef = useRef(false);
  const preferScrollToBottomRef = useRef(false);
  const restoreCancelledRef = useRef(false);
  const blockAutoScrollToBottomRef = useRef(false);
  const lastMessageFingerprintRef = useRef("");
  const stickToBottomRef = useRef(false);
  const scrollRetryTimersRef = useRef([]);
  const [scrollReady, setScrollReady] = useState(false);
  const scrollReadyTimeoutRef = useRef(null);
  const [footerHeight, setFooterHeight] = useState(60);

  useEffect(() => {
    const footer = document.querySelector(".chat-content-footer");
    if (!footer) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.target === footer) {
          setFooterHeight(entry.contentRect.height);
        }
      }
    });

    resizeObserver.observe(footer);
    setFooterHeight(footer.getBoundingClientRect().height);

    return () => resizeObserver.disconnect();
  }, []);

  const revealScrollArea = useCallback(() => {
    if (scrollReadyTimeoutRef.current) {
      clearTimeout(scrollReadyTimeoutRef.current);
    }
    scrollReadyTimeoutRef.current = setTimeout(() => {
      setScrollReady(true);
    }, 50);
  }, []);
  const [flashingMessageId, setFlashingMessageId] = useState(null);
  const flashTimeoutRef = useRef(null);

  const clearMessageFlash = useCallback(() => {
    if (flashTimeoutRef.current) {
      window.clearTimeout(flashTimeoutRef.current);
      flashTimeoutRef.current = null;
    }
    setFlashingMessageId(null);
  }, []);

  const triggerMessageFlash = useCallback((messageId) => {
    if (!messageId) {
      return;
    }

    if (flashTimeoutRef.current) {
      window.clearTimeout(flashTimeoutRef.current);
    }

    setFlashingMessageId(String(messageId));
    flashTimeoutRef.current = window.setTimeout(() => {
      setFlashingMessageId(null);
      flashTimeoutRef.current = null;
    }, 2200);
  }, []);

  const findMessageElement = useCallback((container, messageId) => {
    if (!container || !messageId) {
      return null;
    }

    const id = String(messageId);
    return (
      container.querySelector(`[data-message-id="${id}"]`)
      || container.querySelector(`[data-message-wamid="${id}"]`)
    );
  }, []);

  const scrollUserId = UserData?.id;

  const [Listpresets, setListPresets] = useState([]);
  const [ListCarousel, setListCarousel] = useState([]);
  const [downloadingIds, setDownloadingIds] = useState(() => new Set());
  const [videoPlayer, setVideoPlayer] = useState({
    open: false,
    url: "",
    title: "",
  });
  const [mediaGroupDrawer, setMediaGroupDrawer] = useState({
    open: false,
    messageIds: [],
  });
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const showScrollToBottomRef = useRef(false);

  // Sync ref with state
  useEffect(() => {
    showScrollToBottomRef.current = showScrollToBottom;
  }, [showScrollToBottom]);
  //
  const [hasMore, setHasMore] = useState(true);
  useEffect(() => {
    if (onRegisterSetHasMore) {
      onRegisterSetHasMore(setHasMore);
    }
    return () => {
      if (onRegisterSetHasMore) {
        onRegisterSetHasMore(null);
      }
    };
  }, [onRegisterSetHasMore, setHasMore]);

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  const toContactKey = useCallback(
    (targetContactId) => (targetContactId == null ? "" : String(targetContactId)),
    []
  );

  const clearRestoreRetryTimers = useCallback(() => {
    restoreCancelledRef.current = true;
    if (restoreRetryTimeoutRef.current) {
      clearTimeout(restoreRetryTimeoutRef.current);
      restoreRetryTimeoutRef.current = null;
    }
    isRestoringScrollRef.current = false;
  }, []);

  const clearScrollRetryTimers = useCallback(() => {
    scrollRetryTimersRef.current.forEach((timerId) => clearTimeout(timerId));
    scrollRetryTimersRef.current = [];
  }, []);

  const getDistanceFromBottom = useCallback(() => {
    const scrollElement = chatBodyRef.current?.view;
    if (!scrollElement) {
      return 0;
    }

    return (
      scrollElement.scrollHeight
      - scrollElement.scrollTop
      - scrollElement.clientHeight
    );
  }, []);

  const isNearBottom = useCallback(
    () => getDistanceFromBottom() < SCROLL_BOTTOM_THRESHOLD,
    [getDistanceFromBottom]
  );

  const syncAtBottomUiState = useCallback(() => {
    const atBottom = isNearBottom();
    showScrollToBottomRef.current = !atBottom;
    setShowScrollToBottom(!atBottom);
    if (atBottom) {
      preferScrollToBottomRef.current = true;
    }
  }, [isNearBottom]);

  const scrollChatToBottom = useCallback((behavior = "auto") => {
    if (!chatBodyRef.current) {
      return;
    }

    if (behavior === "auto" && typeof chatBodyRef.current.scrollToBottom === "function") {
      chatBodyRef.current.scrollToBottom();
      return;
    }

    const scrollElement = chatBodyRef.current.view;
    if (!scrollElement) {
      return;
    }

    scrollElement.scrollTo({
      top: scrollElement.scrollHeight,
      behavior,
    });
  }, []);

  const scrollChatToBottomWithRetries = useCallback((behavior = "auto") => {
    if (!chatBodyRef.current) {
      return;
    }

    clearScrollRetryTimers();
    skipScrollSaveRef.current = true;
    isProgrammaticScrollRef.current = true;
    preferScrollToBottomRef.current = true;
    stickToBottomRef.current = true;
    setShowScrollToBottom(false);
    showScrollToBottomRef.current = false;

    const delays = [0, 50, 150, 300, 500, 800, 1200];
    delays.forEach((delay) => {
      const run = () => scrollChatToBottom(behavior);
      if (delay === 0) {
        requestAnimationFrame(() => requestAnimationFrame(run));
        return;
      }

      const timerId = setTimeout(run, delay);
      scrollRetryTimersRef.current.push(timerId);
    });

    const finalizeTimerId = setTimeout(() => {
      skipScrollSaveRef.current = false;
      isProgrammaticScrollRef.current = false;
      syncAtBottomUiState();
      setTimeout(() => {
        stickToBottomRef.current = false;
      }, 1500);
    }, 1300);
    scrollRetryTimersRef.current.push(finalizeTimerId);
  }, [clearScrollRetryTimers, scrollChatToBottom, syncAtBottomUiState]);

  useImperativeHandle(ref, () => ({
    scrollToMessageId: (messageId) => {
      if (!messageId || !chatBodyRef.current?.view) {
        return;
      }

      const container = chatBodyRef.current.view;
      const target = findMessageElement(container, messageId);
      if (!target) {
        return;
      }

      isProgrammaticScrollRef.current = true;
      triggerMessageFlash(messageId);

      const targetOffsetTop =
        target.offsetTop -
        (container.offsetTop || 0);

      const centeredScrollTop =
        targetOffsetTop - (container.clientHeight / 2) + (target.offsetHeight / 2);

      container.scrollTo({ top: centeredScrollTop, behavior: "smooth" });

      window.setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, 450);
    },
    scrollToBottom: (behavior = "smooth") => {
      if (!chatBodyRef.current) {
        return;
      }

      clearRestoreRetryTimers();
      blockAutoScrollToBottomRef.current = false;
      preferScrollToBottomRef.current = true;
      setShowScrollToBottom(false);
      showScrollToBottomRef.current = false;
      skipScrollSaveRef.current = true;
      isProgrammaticScrollRef.current = true;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollChatToBottomWithRetries(behavior === "auto" ? "auto" : "smooth");
          window.setTimeout(() => {
            skipScrollSaveRef.current = false;
            isProgrammaticScrollRef.current = false;
          }, 1400);
        });
      });
    },
  }), [clearRestoreRetryTimers, findMessageElement, scrollChatToBottomWithRetries, triggerMessageFlash]);

  useEffect(() => () => {
    clearMessageFlash();
  }, [clearMessageFlash]);

  const rememberScrollState = useCallback(
    (targetContactId, state) => {
      const key = toContactKey(targetContactId);
      if (!key || !state) {
        return;
      }

      scrollStateByContactRef.current[key] = state;

      if (scrollUserId) {
        saveChatScrollPosition(scrollUserId, key, state);
      }
    },
    [scrollUserId, toContactKey]
  );

  const commitRestoredScrollState = useCallback(
    (targetContactId, saved) => {
      if (!saved) {
        return;
      }

      rememberScrollState(targetContactId, {
        distanceFromBottom: saved.distanceFromBottom,
        atBottom: saved.atBottom,
        currentPage: saved.currentPage || currentPageRef.current,
      });
    },
    [rememberScrollState]
  );

  const clearSavedScrollPosition = useCallback(
    (targetContactId = contactId) => {
      const key = toContactKey(targetContactId);
      if (!key) {
        return;
      }

      delete scrollStateByContactRef.current[key];
      pendingRestoreRef.current = null;

      if (scrollUserId) {
        clearChatScrollPosition(scrollUserId, key);
      }
    },
    [contactId, scrollUserId, toContactKey]
  );

  const persistScrollPosition = useCallback(
    (targetContactId = contactIdRef.current) => {
      if (!scrollUserId || !targetContactId) {
        return;
      }

      const key = toContactKey(targetContactId);
      const cached = scrollStateByContactRef.current[key];
      if (cached) {
        saveChatScrollPosition(scrollUserId, key, cached);
      }
    },
    [scrollUserId, toContactKey]
  );

  const loadSavedScrollPosition = useCallback(
    (targetContactId = contactId) => {
      const key = toContactKey(targetContactId);
      if (!key) {
        return null;
      }

      return loadChatScrollPosition(scrollUserId, key);
    },
    [contactId, scrollUserId, toContactKey]
  );

  const applySavedScrollPosition = useCallback(() => {
    if (!chatBodyRef.current || !contactId) {
      return false;
    }

    const saved = pendingRestoreRef.current;
    const targetPage = saved?.currentPage || 1;
    if (restoringPagesRef.current && currentPageRef.current < targetPage) {
      return false;
    }

    const scrollElement = chatBodyRef.current.view;
    if (!scrollElement) {
      return false;
    }

    const contactKey = toContactKey(contactId);

    if (
      saved &&
      !saved.atBottom &&
      saved.distanceFromBottom >= SCROLL_BOTTOM_THRESHOLD &&
      scrollRestoredForContactRef.current !== contactKey
    ) {
      const applyOnce = () => {
        if (!chatBodyRef.current?.view) {
          return;
        }

        const element = chatBodyRef.current.view;
        isProgrammaticScrollRef.current = true;
        element.scrollTop = Math.max(
          0,
          element.scrollHeight -
          element.clientHeight -
          saved.distanceFromBottom
        );
        requestAnimationFrame(() => {
          isProgrammaticScrollRef.current = false;
        });
      };

      restoreCancelledRef.current = false;

      if (restoreRetryTimeoutRef.current) {
        clearTimeout(restoreRetryTimeoutRef.current);
        restoreRetryTimeoutRef.current = null;
      }

      isRestoringScrollRef.current = true;
      applyOnce();
      commitRestoredScrollState(contactId, saved);
      setShowScrollToBottom(true);
      showScrollToBottomRef.current = true;
      scrollRestoredForContactRef.current = contactKey;

      let attempts = 0;
      const retryRestore = () => {
        if (
          restoreCancelledRef.current ||
          scrollRestoredForContactRef.current !== contactKey ||
          !chatBodyRef.current?.view
        ) {
          isRestoringScrollRef.current = false;
          return;
        }

        applyOnce();
        commitRestoredScrollState(contactId, saved);
        attempts += 1;
        if (attempts < 12) {
          restoreRetryTimeoutRef.current = setTimeout(retryRestore, 200);
          return;
        }

        restoreRetryTimeoutRef.current = setTimeout(() => {
          isRestoringScrollRef.current = false;
        }, 250);
      };
      restoreRetryTimeoutRef.current = setTimeout(retryRestore, 200);
      blockAutoScrollToBottomRef.current = false;

      return true;
    }

    if (
      saved &&
      !saved.atBottom &&
      saved.distanceFromBottom >= SCROLL_BOTTOM_THRESHOLD
    ) {
      return false;
    }

    blockAutoScrollToBottomRef.current = false;

    if (!showScrollToBottomRef.current && !blockAutoScrollToBottomRef.current) {
      scrollChatToBottom();
    }
    return false;
  }, [commitRestoredScrollState, contactId, scrollChatToBottom, toContactKey]);

  useEffect(() => {
    const openedContactKey = toContactKey(contactId);
    const scrollStateByContact = scrollStateByContactRef.current;
    contactIdRef.current = contactId;
    restoreCancelledRef.current = false;
    pagesRestoreDoneRef.current = null;
    scrollRestoredForContactRef.current = null;
    preferScrollToBottomRef.current = false;
    lastMessageFingerprintRef.current = "";
    stickToBottomRef.current = false;
    pendingRestoreRef.current = loadChatScrollPosition(
      scrollUserId,
      openedContactKey,
    );

    const saved = pendingRestoreRef.current;
    const wasScrolledUp = Boolean(saved && !saved.atBottom);
    blockAutoScrollToBottomRef.current = Boolean(
      saved &&
      !saved.atBottom &&
      saved.distanceFromBottom >= SCROLL_BOTTOM_THRESHOLD
    );
    setShowScrollToBottom(wasScrolledUp);
    showScrollToBottomRef.current = wasScrolledUp;

    setScrollReady(false);
    if (scrollReadyTimeoutRef.current) {
      clearTimeout(scrollReadyTimeoutRef.current);
      scrollReadyTimeoutRef.current = null;
    }

    setCurrentPage(1);
    currentPageRef.current = 1;
    setHasMore(true);

    return () => {
      clearRestoreRetryTimers();
      clearScrollRetryTimers();
      if (scrollReadyTimeoutRef.current) {
        clearTimeout(scrollReadyTimeoutRef.current);
        scrollReadyTimeoutRef.current = null;
      }

      // Auth may not be hydrated yet on hard refresh — skip save to avoid
      // overwriting sessionStorage with a default bottom position.
      if (!scrollUserId) {
        return;
      }

      if (preferScrollToBottomRef.current) {
        clearSavedScrollPosition(openedContactKey);
        return;
      }

      const cached = scrollStateByContact[openedContactKey];
      if (cached) {
        rememberScrollState(openedContactKey, cached);
      }
    };
  }, [
    clearRestoreRetryTimers,
    clearScrollRetryTimers,
    contactId,
    clearSavedScrollPosition,
    rememberScrollState,
    scrollUserId,
    toContactKey,
  ]);

  useEffect(() => {
    if (!contactId || loading || !scrollUserId) {
      return undefined;
    }

    const saved = pendingRestoreRef.current;
    if (!saved?.currentPage || saved.currentPage <= 1) {
      return undefined;
    }
    if (pagesRestoreDoneRef.current === contactId) {
      return undefined;
    }

    let cancelled = false;
    restoringPagesRef.current = true;

    (async () => {
      let page = 1;
      let hasMorePages = true;

      while (page < saved.currentPage && hasMorePages && !cancelled) {
        let nextHasMore = false;
        await getChats(contactId, false, page + 1, true, (value) => {
          nextHasMore = value;
          setHasMore(value);
        });
        hasMorePages = nextHasMore;
        page += 1;
        if (!cancelled) {
          setCurrentPage(page);
          currentPageRef.current = page;
        }
      }

      if (!cancelled) {
        pagesRestoreDoneRef.current = contactId;
        restoringPagesRef.current = false;
        applySavedScrollPosition();
        revealScrollArea();
      }
    })();

    return () => {
      cancelled = true;
      restoringPagesRef.current = false;
    };
  }, [applySavedScrollPosition, contactId, getChats, loading, revealScrollArea, scrollUserId]);

  useEffect(() => {
    if (loading || !contactId) {
      return undefined;
    }

    const fallbackTimer = setTimeout(() => {
      setScrollReady(true);
    }, 700);

    return () => clearTimeout(fallbackTimer);
  }, [contactId, loading, messages?.length]);

  useEffect(() => {
    const flushScrollPosition = () => persistScrollPosition();

    window.addEventListener("beforeunload", flushScrollPosition);
    window.addEventListener("pagehide", flushScrollPosition);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        flushScrollPosition();
      }
    });

    return () => {
      window.removeEventListener("beforeunload", flushScrollPosition);
      window.removeEventListener("pagehide", flushScrollPosition);
    };
  }, [persistScrollPosition]);

  const handleMore = useCallback(async () => {
    if (!contactId) {
      return;
    }

    setIsLoadingMore(true);
    justLoadedMoreRef.current = true;
    try {
      await getChats(contactId, false, currentPage + 1, true, setHasMore);
      setCurrentPage((prev) => {
        const next = prev + 1;
        currentPageRef.current = next;
        return next;
      });
    } catch (error) {
      console.error("Error loading more messages:", error);
      justLoadedMoreRef.current = false;
    } finally {
      setIsLoadingMore(false);
    }
  }, [contactId, currentPage, getChats]);

  useEffect(() => {
    if (api && UserData?.id && authToken) {
      GetPresets(() => { }, api, UserData, authToken, setListPresets);
      GetCaruselPresets(() => { }, api, UserData, authToken, setListCarousel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, UserData?.id, authToken]);

  useEffect(() => {
    if (loading || !contactId) {
      return undefined;
    }

    const restoreDelay = blockAutoScrollToBottomRef.current ? 600 : 300;

    const timeout = setTimeout(() => {
      if (!chatBodyRef.current) {
        revealScrollArea();
        return;
      }

      const contactKey = toContactKey(contactId);
      if (!pendingRestoreRef.current && scrollUserId) {
        pendingRestoreRef.current = loadSavedScrollPosition(contactId);
        const saved = pendingRestoreRef.current;
        const wasScrolledUp = Boolean(saved && !saved.atBottom);
        blockAutoScrollToBottomRef.current = Boolean(
          saved &&
          !saved.atBottom &&
          saved.distanceFromBottom >= SCROLL_BOTTOM_THRESHOLD
        );
        showScrollToBottomRef.current = wasScrolledUp;
        setShowScrollToBottom(wasScrolledUp);
      }

      if (currentPage > 1) {
        if (justLoadedMoreRef.current) {
          const scrollElement = chatBodyRef.current.view;
          scrollElement.scrollTop = 100;
          justLoadedMoreRef.current = false;
        }
        revealScrollArea();
        return;
      }

      const saved = pendingRestoreRef.current;
      const targetPage = saved?.currentPage || 1;
      if (restoringPagesRef.current && currentPage < targetPage) {
        return;
      }

      const shouldStayScrolledUp = Boolean(
        saved && !saved.atBottom && saved.distanceFromBottom >= SCROLL_BOTTOM_THRESHOLD
      );

      if (scrollRestoredForContactRef.current !== contactKey) {
        applySavedScrollPosition();
      } else if (
        !showScrollToBottomRef.current &&
        !shouldStayScrolledUp &&
        !blockAutoScrollToBottomRef.current
      ) {
        scrollChatToBottomWithRetries();
      }

      revealScrollArea();
    }, restoreDelay);

    if (!loading) {
      setLoading(false);
    }

    return () => clearTimeout(timeout);
  }, [
    applySavedScrollPosition,
    contactId,
    currentPage,
    loading,
    messages,
    loadSavedScrollPosition,
    revealScrollArea,
    scrollChatToBottomWithRetries,
    setLoading,
    toContactKey,
    scrollUserId,
  ]);

  useEffect(() => {
    if (loading || !scrollReady || !contactId || !messages?.length) {
      return;
    }

    const lastMessage = getLastConversationMessage(messages);
    const fingerprint = getMessageFingerprint(lastMessage);
    if (!fingerprint) {
      return;
    }

    if (!lastMessageFingerprintRef.current) {
      lastMessageFingerprintRef.current = fingerprint;
      return;
    }

    if (fingerprint === lastMessageFingerprintRef.current) {
      return;
    }

    lastMessageFingerprintRef.current = fingerprint;

    if (showScrollToBottomRef.current || blockAutoScrollToBottomRef.current) {
      return;
    }

    scrollChatToBottomWithRetries("auto");
  }, [contactId, loading, messages, scrollChatToBottomWithRetries, scrollReady]);

  useEffect(() => {
    if (!scrollReady || !chatBodyRef.current?.view) {
      return undefined;
    }

    const scrollElement = chatBodyRef.current.view;
    const contentElement = scrollElement.firstElementChild;
    if (!contentElement) {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      if (blockAutoScrollToBottomRef.current) {
        return;
      }

      const shouldStick =
        stickToBottomRef.current
        || preferScrollToBottomRef.current
        || !showScrollToBottomRef.current;

      if (!shouldStick) {
        return;
      }

      isProgrammaticScrollRef.current = true;
      scrollChatToBottom("auto");
      requestAnimationFrame(() => {
        isProgrammaticScrollRef.current = false;
        syncAtBottomUiState();
      });
    });

    observer.observe(contentElement);

    return () => observer.disconnect();
  }, [contactId, messages, scrollChatToBottom, scrollReady, syncAtBottomUiState]);

  const formatDate = useCallback((dateString) => {
    const today = moment().startOf("day");
    const yesterday = moment().subtract(1, "days").startOf("day");
    const messageDate = moment(dateString).startOf("day");

    if (messageDate.isSame(today, "day")) return "Today";
    if (messageDate.isSame(yesterday, "day")) return "Yesterday";
    return messageDate.format("MMMM D, YYYY");
  }, []);

  const groupedMessages = useMemo(() => {
    const grouped = {};
    messages.forEach((message) => {
      const date = formatDate(message.time || message.created_at);
      if (!grouped[date]) {
        grouped[date] = [];
      }
      // Check if message already exists in this date group to avoid duplicates.
      // A message can arrive via two different socket events with different id types
      // (e.g. wamid string from handleIncomingMessage vs DB integer from handleMenuUpdated),
      // so we compare both `id` and `message_id` cross-ways.
      const messageExists = grouped[date].some(
        (msg) =>
          (message.id && msg.id === message.id) ||
          (message.message_id && msg.message_id === message.message_id) ||
          (message.message_id && msg.id === message.message_id) ||
          (message.id && msg.message_id === message.id)
      );
      if (!messageExists) {
        grouped[date].push(message);
      }
    });

    // Sort messages within each date group by timestamp
    Object.keys(grouped).forEach((date) => {
      grouped[date].sort(
        (a, b) =>
          new Date(a.time || a.created_at) - new Date(b.time || b.created_at)
      );
    });

    // Return an array of entries to keep the reference stable
    return Object.entries(grouped);
  }, [messages, formatDate]);

  //const groupedMessages = useMemo(() => groupMessagesByDate(messages), [messages, formatDate]);

  const isChatBotMessage = (elm) => {
    if (!elm) {
      return false;
    }
    if (elm.is_chatbot_message) {
      return true;
    }
    return Boolean(elm.text && keywords?.includes(elm.text));
  };

  const getType = (message) => {
    if (isLocationMessage(message)) {
      return "location";
    }

    const from = message?.from;
    return from === "opposite" ? message?.msgType : message?.type;
  };

  const fetchIncomingMediaUrl = useCallback(
    async (message) => {
      const mediaId = message?.media_id;
      const reportId = message?.id;
      if (!mediaId || !reportId) {
        return null;
      }

      const firstResponse = await apiClient.get(`messaging/media/${mediaId}`);
      const metaUrl = firstResponse.data?.url;
      if (!metaUrl) {
        return null;
      }

      const secondResponse = await apiClient.post(
        `${api}retrieve-image-from-meta`,
        {
          url: metaUrl,
          parent_number: String(parentNumber || UserData?.whatsapp_number || ""),
          child_number: String(number ?? ""),
          report_id: reportId,
          media_type: getType(message),
          mime_type: message?.mime_type || firstResponse.data?.mime_type,
          file_name: message?.file_name,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      return secondResponse?.data?.media_url ?? null;
    },
    [api, authToken, parentNumber, UserData?.whatsapp_number, number]
  );

  const ensureIncomingMediaUrl = useCallback(
    async (message) => {
      if (message?.media_url) {
        return message.media_url;
      }

      const text = String(message?.text || "");
      if (/^https?:\/\//i.test(text)) {
        return text;
      }

      if (!message?.media_id || !message?.id) {
        return null;
      }

      const media_url = await fetchIncomingMediaUrl(message);
      if (media_url && onUpdateMessage) {
        onUpdateMessage(message.id, { media_url });
      }

      return media_url;
    },
    [fetchIncomingMediaUrl, onUpdateMessage]
  );

  const handleDownloadMedia = async (message) => {
    const reportId = message?.id;
    if (!message?.media_id || !reportId) {
      return;
    }

    setDownloadingIds((prev) => new Set(prev).add(reportId));
    try {
      await ensureIncomingMediaUrl(message);
    } catch (error) {
      console.error("Error downloading media:", error);
    } finally {
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.delete(reportId);
        return next;
      });
    }
  };

  const handleDownloadAudio = async (message) => {
    const reportId = message?.id;
    if (!reportId) {
      return;
    }

    setDownloadingIds((prev) => new Set(prev).add(reportId));
    try {
      const mediaUrl = await ensureIncomingMediaUrl(message);
      if (!mediaUrl) {
        return;
      }

      await downloadReportMediaFile({
        api,
        authToken,
        reportId,
        fileName: resolveAudioDownloadName(message, mediaUrl),
        fallbackUrl: mediaUrl,
      });
    } catch (error) {
      console.error("Error downloading audio:", error);
    } finally {
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.delete(reportId);
        return next;
      });
    }
  };

  const resolveAudioMedia = useCallback(
    async (message) => {
      const reportId = message?.id;
      if (!reportId) {
        return null;
      }

      setDownloadingIds((prev) => new Set(prev).add(reportId));
      try {
        return await ensureIncomingMediaUrl(message);
      } catch (error) {
        console.error("Error loading audio:", error);
        return null;
      } finally {
        setDownloadingIds((prev) => {
          const next = new Set(prev);
          next.delete(reportId);
          return next;
        });
      }
    },
    [ensureIncomingMediaUrl]
  );

  const renderIncomingImage = (message, compact = false, inGrid = false, isOverflowCell = false, isMobile = false, inDrawer = false) => (
    <IncomingImagePreview
      message={message}
      compact={compact}
      inGrid={inGrid}
      isOverflowCell={isOverflowCell}
      isDownloading={downloadingIds.has(message?.id)}
      onDownload={() => handleDownloadMedia(message)}
      isMobile={isMobile}
      inDrawer={inDrawer}
    />
  );

  const openMediaGroupDrawer = (messages) => {
    const ids = messages.map((m) => m.id || m.message_id).filter(Boolean);
    setMediaGroupDrawer({ open: true, messageIds: ids });
  };

  const closeMediaGroupDrawer = () => {
    setMediaGroupDrawer({ open: false, messageIds: [] });
  };

  // Derive live message objects from the main messages prop so that updates
  // (e.g. media_url set after download) are reflected immediately in the drawer.
  const liveDrawerMessages = useMemo(() => {
    if (!mediaGroupDrawer.open || mediaGroupDrawer.messageIds.length === 0) {
      return [];
    }
    const idSet = new Set(mediaGroupDrawer.messageIds.map(String));
    return messages.filter(
      (m) => idSet.has(String(m.id)) || idSet.has(String(m.message_id))
    );
  }, [mediaGroupDrawer.open, mediaGroupDrawer.messageIds, messages]);

  const renderMediaContent = (message, { compact = false, inGrid = false, isOverflowCell = false, isMobile = false, inDrawer = false } = {}) => {
    const from = message?.from;
    const type = getType(message);
    const url = resolveMessageMediaUrl(message);
    const canPreviewImage = Boolean(url) && !isOverflowCell;
    const isDownloaded = !!url;
    const mediaStyle = compact
      ? {
        width: "100%",
        height: (inDrawer && !isMobile && isDownloaded) ? "auto" : "100%",
        minHeight: (inDrawer && !isMobile && isDownloaded) ? undefined : 120,
        objectFit: (inDrawer && !isMobile && isDownloaded) ? "contain" : "cover",
        display: "block",
      }
      : { height: "200px", objectFit: "cover", borderRadius: "8px" };

    switch (type) {
      case "image":
      case "sticker":
        if (from === "opposite" && !url) {
          return renderIncomingImage(message, compact, inGrid, isOverflowCell, isMobile, inDrawer);
        }
        return (
          <div
            style={{
              position: "relative",
              display: compact ? "block" : "inline-block",
              width: compact ? "100%" : undefined,
              height: (compact && (isMobile || !inDrawer)) ? "100%" : undefined,
            }}
          >
            <Image
              src={url || MEDIA_PLACEHOLDER}
              alt="message"
              loading="lazy"
              style={mediaStyle}
              preview={canPreviewImage}
            />
          </div>
        );
      case "video":
        if (from === "opposite" && !url) {
          if (compact) {
            return renderIncomingImage(message, true, inGrid, isOverflowCell, isMobile, inDrawer);
          }
          return renderFileAttachment(message);
        }
        return (
          <VideoPreviewThumbnail
            url={url}
            title={message?.file_name || "Video"}
            maxWidth={compact ? "100%" : 280}
            maxHeight={compact ? 240 : 180}
            onClick={() => openVideoPlayer(url, message?.file_name || "Video")}
          />
        );
      default:
        return null;
    }
  };

  const openVideoPlayer = (url, title) => {
    if (!url) {
      return;
    }
    setVideoPlayer({
      open: true,
      url,
      title: title || "Video",
    });
  };

  const closeVideoPlayer = () => {
    setVideoPlayer({
      open: false,
      url: "",
      title: "",
    });
  };

  const handleDownloadFile = async (message, url) => {
    const reportId = message?.id;
    if (!reportId) {
      return;
    }

    setDownloadingIds((prev) => new Set(prev).add(reportId));
    try {
      const meta = resolveDocumentMeta(message);
      await downloadReportMediaFile({
        api,
        authToken,
        reportId,
        fileName: meta.fileName || undefined,
        fallbackUrl: url,
      });
    } catch (error) {
      console.error("Error downloading file:", error);
    } finally {
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.delete(reportId);
        return next;
      });
    }
  };

  const renderFileAttachment = (message, url = null) => {
    const meta = resolveDocumentMeta(message);
    const reportId = message?.id;

    return (
      <FileAttachmentCard
        meta={meta}
        url={url || undefined}
        downloadName={meta.fileName || undefined}
        onDownload={url ? undefined : () => handleDownloadMedia(message)}
        onDownloadFile={
          url && reportId ? () => handleDownloadFile(message, url) : undefined
        }
        isDownloading={downloadingIds.has(reportId)}
      />
    );
  };

  const handleJumpToQuotedMessage = useCallback((messageId) => {
    if (!messageId) {
      return;
    }
    if (typeof ref?.current?.scrollToMessageId === "function") {
      ref.current.scrollToMessageId(messageId);
    }
  }, [ref]);

  const renderTextContent = (message) => {
    const text = message?.text;
    if (!text) {
      return <span></span>;
    }

    if (
      activeSearchMatchId
      && message?.id === activeSearchMatchId
      && searchHighlightQuery
    ) {
      return highlightSearchText(text, searchHighlightQuery);
    }

    return renderTextWithLinks(text);
  };

  const renderContent = (message) => {
    let from = message?.from;
    let type = getType(message);
    let url = resolveMessageMediaUrl(message);

    switch (type) {
      case "pending_image":
        return (
          <div style={{ position: "relative", display: "inline-block" }}>
            <Image
              src="/img/others/dummy-header.jpg"
              alt="loading"
              loading="lazy"
              style={{
                height: "200px",
                objectFit: "cover",
                borderRadius: "8px",
                filter: "blur(2px)",
              }}
              preview={false}
            />
            <Spin
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            />
          </div>
        );
      case "image":
      case "sticker":
      case "video":
        return renderMediaContent(message);
      case "document":
        return renderFileAttachment(message, url);
      case "audio":
        return (
          <IncomingAudioMessage
            message={message}
            isOutgoing={from === "me"}
            isFetching={downloadingIds.has(message?.id)}
            isDownloading={downloadingIds.has(message?.id)}
            onFetchMedia={resolveAudioMedia}
            onDownload={
              from === "opposite" ? () => handleDownloadAudio(message) : undefined
            }
          />
        );
      case "location":
        return <LocationMessage message={message} />;
      case "template":
        return (
          <RanderTemplatePreview
            text={message?.text}
            presetName={message?.preset_name}
            bodyValues={message?.body_values}
          />
        );
      case "li_preset":
        return (
          <RanderListItemPreview
            text={message?.text}
            presetName={message?.preset_name}
            presets={Listpresets}
          />
        );
      case "cr_preset":
        return (
          <RanderCarouselPreview text={message?.text} presets={ListCarousel} />
        );
      case "text":
        return renderTextContent(message);
      default:
        return renderTextContent(message);
    }
  };

  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "template":
        return (
          <FileTextOutlined style={{ color: "#1890ff", marginLeft: "5px" }} />
        );
      case "li_preset":
        return (
          <UnorderedListOutlined
            style={{ color: "#52c41a", marginLeft: "5px" }}
          />
        );
      case "cr_preset":
        return (
          <AppstoreOutlined style={{ color: "#fa8c16", marginLeft: "5px" }} />
        );
      default:
        return (
          <MessageOutlined style={{ color: "#8c8c8c", marginLeft: "5px" }} />
        );
    }
  };

  const customBubbles = ["template", "li_preset", "cr_preset"];

  const AgentContent = ({ data, activeChatCount }) => {
    const activeDays =
      data?.working_days?.split(",").map((day) => day.trim()) || [];
    return (
      <>
        {data && (
          <Row>
            <Col className="d-flex align-items-center mb-3">
              <Avatar
                size={40}
                style={{
                  backgroundColor: "#87d068",
                  fontSize: "15px",
                }}
              >
                {getInitials(data?.name)}
              </Avatar>
              <Title
                level={5}
                style={{ margin: "0 0 0 0.5rem", textAlign: "center" }}
              >
                {data?.name}
              </Title>
            </Col>
            <Col span={24} className="mb-2">
              <Space
                direction="vertical"
                size="small"
                style={{ width: "100%" }}
              >
                <Row align="middle">
                  <Col flex="24px">
                    <MessageOutlined style={{ color: "#8c8c8c" }} />
                  </Col>
                  <Col flex="auto">
                    <Tag className="text-primary">
                      Active Chats : {activeChatCount}
                    </Tag>
                  </Col>
                </Row>
                <Row align="middle">
                  <Col flex="24px">
                    <MailOutlined style={{ color: "#8c8c8c" }} />
                  </Col>
                  <Col flex="auto">
                    <Text copyable>{data?.email}</Text>
                  </Col>
                </Row>
                <Row align="middle">
                  <Col flex="24px">
                    <PhoneOutlined style={{ color: "#8c8c8c" }} />
                  </Col>
                  <Col flex="auto">
                    <Text copyable>{data?.phone}</Text>
                  </Col>
                </Row>
              </Space>
            </Col>
            <Col span={24}>
              <Row
                className="d-flex gap-3"
                align="middle"
                style={{ marginBottom: "8px" }}
              >
                <Col className="d-flex" style={{ gap: "0.7rem" }}>
                  <CalendarOutlined style={{ color: "#8c8c8c" }} />
                  <Flex
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "4px",
                      maxWidth: "100%",
                    }}
                  >
                    {activeDays?.map((day) => (
                      <Tag
                        key={day}
                        color="blue"
                        style={{
                          borderRadius: "12px",
                          padding: "0 8px",
                          margin: 0,
                          fontSize: "11px",
                          lineHeight: "16px",
                        }}
                      >
                        {day}
                      </Tag>
                    ))}
                  </Flex>
                </Col>
              </Row>
            </Col>
          </Row>
        )}
      </>
    );
  };

  const getMessageMenuItems = useCallback((msgObj) => {
    const items = [
      {
        key: "reply",
        icon: <HiMiniArrowTurnUpLeft />,
        label: "Reply",
        style: { paddingLeft: 16, paddingRight: 16 },
      },
      {
        key: "copy",
        icon: <CopyOutlined />,
        label: "Copy",
        style: { paddingLeft: 16, paddingRight: 16 },
      },
    ];

    if (hasRestrictedOriginalMessage(msgObj)) {
      items.push({
        key: "copy_original",
        icon: <CopyOutlined />,
        label: "Copy original text",
        style: { paddingLeft: 16, paddingRight: 16 },
      });
    }

    return items;
  }, []);

  const fallbackCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      const successful = document.execCommand("copy");
      if (successful) {
        antdMessage.success("Copied to clipboard!");
      } else {
        antdMessage.error("Failed to copy!");
      }
    } catch (err) {
      console.error("Fallback copy failed", err);
      antdMessage.error("Failed to copy!");
    }
    document.body.removeChild(textArea);
  };

  const handleMenuClick = useCallback(({ key }, msgObj) => {
    if (key === "reply") {
      if (typeof onReply === "function") {
        onReply(msgObj);
      }
    } else if (key === "copy") {
      const textToCopy = msgObj?.text || msgObj?.caption || "";
      if (!textToCopy) {
        antdMessage.warning("No text to copy!");
        return;
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(textToCopy)
          .then(() => {
            antdMessage.success("Copied to clipboard!");
          })
          .catch((err) => {
            console.error("Clipboard copy failed: ", err);
            fallbackCopy(textToCopy);
          });
      } else {
        fallbackCopy(textToCopy);
      }
    } else if (key === "copy_original") {
      resolveOriginalMessageForCopy(msgObj).then((textToCopy) => {
        if (!textToCopy) {
          antdMessage.warning("Original text is unavailable. Check RESTRICTED_MESSAGE_KEY config.");
          return;
        }
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(textToCopy)
            .then(() => {
              antdMessage.success("Original text copied!");
            })
            .catch((err) => {
              console.error("Clipboard copy failed: ", err);
              fallbackCopy(textToCopy);
            });
        } else {
          fallbackCopy(textToCopy);
        }
      });
    }
  }, [onReply]);

  const renderMessageActionMenu = (msgObj) => {
    if (isMobile || isChatBotMessage(msgObj)) {
      return null;
    }

    return (
      <div className="msg-hover-action">
        <Dropdown
          menu={{
            items: getMessageMenuItems(msgObj),
            onClick: (info) => handleMenuClick(info, msgObj),
          }}
          placement="bottomRight"
          trigger={["click"]}
        >
          <button
            type="button"
            className="msg-hover-action-btn"
            aria-label="Message actions"
          >
            <MoreOutlined style={{ fontSize: 16 }} />
          </button>
        </Dropdown>
      </div>
    );
  };

  const TimeComp = (type, elm, { showImageExpiry = type === "image" } = {}) => (
    <div
      className={`mt-2 d-flex justify-content-end align-items-center`}
      style={{
        textAlign: "right",
        fontSize: "11px",
        color: "gray",
        gap: 5,
        marginRight: elm.from === "me" ? "8px" : "0px",
      }}
    >
      {showImageExpiry && (
        <>
          <div className="d-flex">
            <span className="text-danger">Expires In 2 Days</span>
          </div>
          <span>/</span>
        </>
      )}
      {isChatBotMessage(elm) && elm.from === "opposite" && (
        <>
          <div className="d-flex">
            <p className="bg-dark text-right m-0 px-1 h6 rounded text-white">
              Chatbot Message
            </p>
          </div>
          <span>/</span>
        </>
      )}
      <div className="d-flex align-items-center gap-1">
        {customBubbles?.includes(getType(elm)) && (
          <span>
            <Popover
              content={
                <div>
                  <p>
                    <strong>Reply Type:</strong> {getTypeIcon(getType(elm))}{" "}
                    {capitalize(getType(elm))}
                  </p>
                  <p>
                    <strong>
                      {getType(elm)?.toLowerCase() === "cr_preset"
                        ? "Preset Name"
                        : getType(elm)?.toLowerCase() === "li_preset"
                          ? "Preset Name"
                          : "Template Name"}
                      :
                    </strong>{" "}
                    {elm.text}
                  </p>
                </div>
              }
              title="Message Details"
              trigger="hover"
              placement="topLeft"
            >
              <span className="cursor-pointer" style={{ alignItems: "center" }}>
                <InfoCircleOutlined
                  style={{ marginRight: "4px", fontSize: "10px" }}
                />
                {`Message Info / `}
              </span>
            </Popover>
          </span>
        )}
        {moment(elm.time || elm.created_at).format("HH:mm")}
        {elm.from === "me" && (
          <span style={{ display: "inline-block" }}>
            {getStatusIcon(elm.status, elm._errorMsg)}
          </span>
        )}
      </div>
    </div>
  );

  const handleScroll = () => {
    if (
      !chatBodyRef.current ||
      isProgrammaticScrollRef.current ||
      isRestoringScrollRef.current
    ) {
      return;
    }

    const scrollElement = chatBodyRef.current.view;
    const distanceFromBottom =
      scrollElement.scrollHeight -
      scrollElement.scrollTop -
      scrollElement.clientHeight;
    const isAtBottom = distanceFromBottom < SCROLL_BOTTOM_THRESHOLD;
    setShowScrollToBottom(!isAtBottom);

    if (!isAtBottom) {
      preferScrollToBottomRef.current = false;
    }

    if (!contactId || skipScrollSaveRef.current) {
      return;
    }

    const state = {
      distanceFromBottom,
      atBottom: isAtBottom,
      currentPage: currentPageRef.current,
    };
    scrollStateByContactRef.current[toContactKey(contactId)] = state;

    if (scrollUserId) {
      saveChatScrollPosition(scrollUserId, toContactKey(contactId), state);
    }
  };

  const handleScrollToBottomClick = () => {
    if (!chatBodyRef.current || !contactId) {
      return;
    }

    clearRestoreRetryTimers();
    clearSavedScrollPosition(contactId);
    preferScrollToBottomRef.current = true;
    blockAutoScrollToBottomRef.current = false;
    scrollRestoredForContactRef.current = null;
    skipScrollSaveRef.current = true;
    isProgrammaticScrollRef.current = true;

    scrollChatToBottomWithRetries("auto");

    setShowScrollToBottom(false);
    showScrollToBottomRef.current = false;

    window.setTimeout(() => {
      skipScrollSaveRef.current = false;
      isProgrammaticScrollRef.current = false;
    }, 300);
  };

  return (
    <>
      <div
        className="chat-content-body"
        style={{
          backgroundImage: `url(${bg})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
        }}
      >
        <div className="chat-content-scroll-area" style={{ position: 'relative' }}>
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10,
              overflow: 'hidden',
              opacity: (!scrollReady || (loading && !messages?.length)) ? 1 : 0,
              pointerEvents: (!scrollReady || (loading && !messages?.length)) ? 'auto' : 'none',
              transition: 'opacity 0.3s ease-out'
            }}
          >
            <ChatSkeleton />
          </div>
          <Scrollbars
            ref={chatBodyRef}
            autoHide
            renderTrackHorizontal={(props) => (
              <div style={{ display: "none" }} {...props} />
            )}
            onScroll={handleScroll}
            style={{
              opacity: scrollReady ? 1 : 0,
              transition: "opacity 0.3s ease-in",
              ...(isMobile ? { height: "100%" } : {}),
            }}
          >
            {/* Top spacer for glassmorphism header */}
            <div style={{ height: '70px', flexShrink: 0 }} />
            {hasMore && (
              <div className="text-center">
                <Button
                  size="small"
                  type="primary"
                  shape="round"
                  className="load-more-chats-btn"
                  icon={<ReloadOutlined style={{ fontSize: 12 }} />}
                  disabled={isLoadingMore || loading}
                  loading={isLoadingMore}
                  onClick={handleMore}
                  style={{
                    background: landingButtonGradient,
                    border: "none",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "12px",
                    height: "26px",
                    padding: "0 12px",
                    margin: "0 0 6px 0",
                    boxShadow: "0 4px 16px rgba(24,144,255,0.15)",
                    transition: "all 0.3s",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  Load More
                </Button>
              </div>
            )}
            {isLoadingMore && <LoadMoreSkeleton />}
            <>
              {groupedMessages.map(([date, msgs]) => (
                <React.Fragment key={`date-${date}`}>
                  {date !== "Invalid date" && (
                    <Divider
                      key={`divider-${date}`}
                      style={{
                        textAlign: "center",
                        margin: "0",
                        fontSize: "12px",
                        color: "#888",
                        fontWeight: "bold",
                      }}
                    >
                      {date}
                    </Divider>
                  )}
                  {groupMessagesForDisplay(msgs).map((item, i) => {
                    if (item.kind === "date") {
                      return null;
                    }

                    const isMediaGroup = item.kind === "media_group";
                    const elm = isMediaGroup ? item.messages[0] : item.message;
                    const timeElm = isMediaGroup
                      ? item.messages[item.messages.length - 1]
                      : item.message;
                    const type = getType(timeElm);
                    const messageType = isMediaGroup ? type : getType(item.message);
                    const isAudioMessage = messageType === "audio";
                    const showImageExpiry = isMediaGroup
                      ? groupContainsImage(item.messages)
                      : type === "image" || type === "sticker";
                    const displayMessage = isMediaGroup ? item.messages[0] : item.message;
                    const quotedPreview = !isMediaGroup && displayMessage?.reply_to ? (
                      <QuotedMessagePreview
                        replyTo={displayMessage.reply_to}
                        contactName={contactName || number}
                        isOutgoingBubble={displayMessage.from === "me"}
                        onJumpToMessage={handleJumpToQuotedMessage}
                      />
                    ) : null;
                    const content = isMediaGroup ? (
                      <MediaGroupGrid
                        messages={item.messages}
                        renderCell={(message, options) =>
                          renderMediaContent(message, options)
                        }
                        onOverflowClick={(messages) => openMediaGroupDrawer(messages)}
                      />
                    ) : (
                      renderContent(item.message)
                    );
                    const itemKey = isMediaGroup
                      ? `group-${item.messages
                        .map((message) => message.id || message.message_id)
                        .join("-")}`
                      : `msg-${elm.id || elm.message_id || `idx-${i}`}`;

                    const isActiveSearchMatch = Boolean(
                      activeSearchMatchId
                      && (elm.id === activeSearchMatchId
                        || (!isMediaGroup && item.message?.id === activeSearchMatchId))
                    );
                    const isFlashingMessage = Boolean(
                      flashingMessageId
                      && (
                        String(elm.id) === flashingMessageId
                        || String(elm.message_id) === flashingMessageId
                        || (isMediaGroup && item.messages.some(
                          (message) =>
                            String(message.id) === flashingMessageId
                            || String(message.message_id) === flashingMessageId
                        ))
                      )
                    );

                    const isSystemEvent = !isMediaGroup
                      && (elm?.msgType === "system" || elm?.type === "system");

                    if (isSystemEvent) {
                      const systemText = formatAssignmentSystemText(
                        elm.text,
                        UserData?.name
                      );

                      return (
                        <div key={itemKey} className="text-center my-2">
                          <Tag bordered={false} color="grey">{systemText}</Tag>
                        </div>
                      );
                    }

                    return (
                      <motion.div
                        key={itemKey}
                        data-message-id={elm.id || undefined}
                        data-message-wamid={elm.message_id || undefined}
                        layout={elm.from === "opposite"}
                        initial={elm.from === "opposite" ? { opacity: 0, translateY: 40, scale: 0.95 } : false}
                        animate={elm.from === "opposite" ? { opacity: 1, translateY: 0, scale: 1 } : {}}
                        exit={elm.from === "opposite" ? { opacity: 0, translateY: 40, scale: 0.95 } : {}}
                        transition={elm.from === "opposite" ? {
                          type: "spring",
                          stiffness: 350,
                          damping: 30,
                          mass: 0.7,
                          duration: 0.45,
                        } : {}}
                        className={`msg ${elm.from === "opposite"
                          ? "msg-recipient"
                          : elm.from === "me"
                            ? "msg-sent"
                            : ""
                          }${isActiveSearchMatch ? " chat-search-match-active" : ""}${isFlashingMessage ? " msg-highlight-flash" : ""}`}
                      >
                        {elm.avatar && (
                          <div className="mr-2">
                            <Avatar src={elm.avatar} />
                          </div>
                        )}
                        <div
                          className={`msg-content-col d-flex ${elm.from === "me"
                            ? "flex-column align-items-end"
                            : "flex-column"
                            }`}
                        >
                          <div className="msg-hover-row">
                            <SwipeToReplyWrapper
                              enabled={
                                isMobile
                                && !isChatBotMessage(elm)
                                && typeof onReply === "function"
                              }
                              onReply={() => onReply(elm)}
                            >
                              <div
                                className={`bubble border-0 rounded-lg ${!elm.avatar ? " ml-1" : ""
                                  } ${elm.from === "me"
                                    ? "bubble-sent"
                                    : "bubble-received"
                                  }`}
                                style={{
                                  padding: isMediaGroup ? "4px" : undefined,
                                  ...(elm.from === "opposite" &&
                                    style.oppositeBubble),
                                  ...(elm.from === "opposite" && isMediaGroup && {
                                    padding: "4px",
                                  }),
                                  ...(elm.from === "me" && {
                                    backgroundColor: "#D9FDD3",
                                  }),
                                  ...(elm.from === "me" &&
                                    !isMediaGroup &&
                                    ["template", "cr_preset", "li_preset"].includes(
                                      getType(elm)
                                    ) && {
                                    backgroundColor: "transparent",
                                    padding: 0,
                                  }),
                                }}
                              >
                                <div
                                  className={isMediaGroup ? "" : isAudioMessage ? "px-2 py-1" : "p-2"}
                                  style={{ maxWidth: "50rem", color: "#000" }}
                                >
                                  {quotedPreview}
                                  {content}
                                </div>
                                {elm.from === "opposite" &&
                                  TimeComp(type, timeElm, { showImageExpiry })}
                              </div>
                            </SwipeToReplyWrapper>
                            {!isMediaGroup
                              && shouldShowOutgoingAgentAvatar(elm, UserData?.id) && (
                              <div className="agent cursor-pointer" style={{ alignSelf: 'center', margin: '0 4px' }}>
                                <Popover
                                  content={
                                    <AgentContent
                                      data={elm.agent_id}
                                      activeChatCount={elm.agent_activeChatCount}
                                    />
                                  }
                                  title="Agent Details"
                                  trigger="hover"
                                  placement="left"
                                  overlayStyle={{
                                    maxWidth: 400,
                                    whiteSpace: "nowrap",
                                    zIndex: 9999,
                                  }}
                                >
                                  <Avatar style={{ backgroundColor: "#87d068" }}>
                                    {getInitials(elm.agent_id?.name)}
                                  </Avatar>
                                </Popover>
                              </div>
                            )}
                            {renderMessageActionMenu(elm)}
                          </div>
                          {elm.from === "me" &&
                            TimeComp(type, timeElm, { showImageExpiry })}
                        </div>
                      </motion.div>
                    );
                  })}
                </React.Fragment>
              ))}
              {/* Bottom spacer for glassmorphism footer */}
              <div style={{ height: '60px', flexShrink: 0 }} />
            </>
          </Scrollbars>
        </div>
        {showScrollToBottom && scrollReady && (
          <motion.div
            className="chat-scroll-to-bottom-fab"
            animate={{
              y: [0, -10, 0], // bounce up and down
            }}
            transition={{
              duration: 1,
              repeat: Infinity,
              repeatType: "loop",
              ease: "easeInOut",
            }}
            style={{
              position: "absolute",
              left: "50%",
              bottom: footerHeight + 12,
              transform: "translateX(-50%)",
              zIndex: 25,
            }}
          >
            <Button
              shape="circle"
              size="small"
              className="border-0"
              icon={<DownOutlined style={{ fontSize: '12px' }} />}
              type="primary"
              onClick={handleScrollToBottomClick}
              style={{
                width: '30px',
                height: '30px',
                minWidth: '30px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: landingButtonGradient,
                boxShadow: "0 2px 8px rgba(0,0,0,0.6)",
                color: "#fff",
              }}
            />
          </motion.div>
        )}
      </div>
      <VideoPlayerModal
        open={videoPlayer.open}
        onClose={closeVideoPlayer}
        url={videoPlayer.url}
        title={videoPlayer.title}
      />
      <MediaGroupViewer
        open={mediaGroupDrawer.open}
        onClose={closeMediaGroupDrawer}
        messages={liveDrawerMessages}
        isMobile={isMobile}
        renderMedia={(message) => renderMediaContent(message, { compact: true, inDrawer: true, isMobile })}
      />
    </>
  );
});

export default ChatContentBody;
